import { ParsedUrlQuery } from "querystring";

import { State, Action, ActionType, Sort } from "@interfaces/filters";

function defaultState(): State {
  return {
    sortType: Sort.RELEVANCY,
    colorSet: new Set<string>(),
    materialSet: new Set<string>(),
    stylesSet: new Set<string>(),
    areasSet: new Set<string>(),
    suppliersSet: new Set<string>(),
    price: [0, 2400],
    lengthQuery: [0, 400],
    heightQuery: [0, 100],
    widthQuery: [0, 400],
    totalNumberOfProducts: 0,
  };
}

export function init(state: Partial<State>): State {
  const initState = defaultState();
  return {
    sortType: state.sortType || initState.sortType,
    colorSet: state.colorSet?.size ? state.colorSet : initState.colorSet,
    materialSet: state.materialSet?.size
      ? state.materialSet
      : initState.materialSet,
    stylesSet: state.stylesSet?.size ? state.stylesSet : initState.stylesSet,
    areasSet: state.areasSet?.size ? state.areasSet : initState.areasSet,
    suppliersSet: state.suppliersSet?.size
      ? state.suppliersSet
      : initState.suppliersSet,
    price: state.price?.length ? state.price : initState.price,
    lengthQuery: state.lengthQuery?.length
      ? state.lengthQuery
      : initState.lengthQuery,
    heightQuery: state.heightQuery?.length
      ? state.heightQuery
      : initState.heightQuery,
    widthQuery: state.widthQuery?.length
      ? state.widthQuery
      : initState.widthQuery,
    priceIsSet: state.priceIsSet || initState.priceIsSet,
    lengthIsSet: state.lengthIsSet || initState.lengthIsSet,
    heightIsSet: state.heightIsSet || initState.heightIsSet,
    widthIsSet: state.widthIsSet || initState.widthIsSet,
    totalNumberOfProducts: state.totalNumberOfProducts || 0,
  };
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionType.SET_SORT:
      return { ...state, sortType: action.value };
    case ActionType.CLEAR_SORT:
      return { ...state, sortType: Sort.RELEVANCY };
    case ActionType.SET_COLOR:
      return { ...state, colorSet: state.colorSet.add(action.value) };
    case ActionType.REMOVE_COLOR:
      state.colorSet.delete(action.value);
      return { ...state, colorSet: new Set(state.colorSet) };
    case ActionType.CLEAR_COLOR:
      return { ...state, colorSet: new Set<string>() };
    case ActionType.SET_MATERIAL:
      return { ...state, materialSet: state.materialSet.add(action.value) };
    case ActionType.REMOVE_MATERIAL:
      state.materialSet.delete(action.value);
      return { ...state, materialSet: new Set(state.materialSet) };
    case ActionType.CLEAR_MATERIAL:
      return { ...state, materialSet: new Set<string>() };
    case ActionType.SET_STYLE:
      return { ...state, stylesSet: state.stylesSet.add(action.value) };
    case ActionType.REMOVE_STYLE:
      state.stylesSet.delete(action.value);
      return { ...state, stylesSet: new Set(state.stylesSet) };
    case ActionType.CLEAR_STYLE:
      return { ...state, stylesSet: new Set<string>() };
    case ActionType.SET_AREA:
      return { ...state, areasSet: state.areasSet.add(action.value) };
    case ActionType.REMOVE_AREA:
      state.areasSet.delete(action.value);
      return { ...state, areasSet: new Set(state.areasSet) };
    case ActionType.CLEAR_AREA:
      return { ...state, areasSet: new Set<string>() };
    case ActionType.SET_SUPPLIER:
      return { ...state, suppliersSet: state.suppliersSet.add(action.value) };
    case ActionType.REMOVE_SUPPLIER:
      state.suppliersSet.delete(action.value);
      return { ...state, suppliersSet: new Set(state.suppliersSet) };
    case ActionType.CLEAR_SUPPLIER:
      return { ...state, suppliersSet: new Set<string>() };
    case ActionType.SET_PRICE:
      return { ...state, price: action.value, priceIsSet: true };
    case ActionType.CLEAR_PRICE:
      return { ...state, price: [0, 2400], priceIsSet: false };
    case ActionType.SET_LENGTH:
      return { ...state, lengthQuery: action.value, lengthIsSet: true };
    case ActionType.CLEAR_LENGTH:
      return { ...state, lengthQuery: [0, 400], lengthIsSet: false };
    case ActionType.SET_HEIGHT:
      return { ...state, heightQuery: action.value, heightIsSet: true };
    case ActionType.CLEAR_HEIGHT:
      return { ...state, heightQuery: [0, 100], heightIsSet: false };
    case ActionType.SET_WIDTH:
      return { ...state, widthQuery: action.value, widthIsSet: true };
    case ActionType.CLEAR_WIDTH:
      return { ...state, widthQuery: [0, 400], widthIsSet: false };
    case ActionType.RESET:
      return defaultState();
    default:
      return { ...state };
  }
}

export const createQuery = (state: State): ParsedUrlQuery => {
  let query = {
    sort: state.sortType.toLowerCase(),
  };

  query = [
    {
      value: Array.from(state.colorSet),
      name: "color",
      isSet: state.colorSet.size !== 0,
    },
    {
      value: Array.from(state.materialSet),
      name: "material",
      isSet: state.materialSet.size !== 0,
    },
    {
      value: Array.from(state.stylesSet),
      name: "styles",
      isSet: state.stylesSet.size !== 0,
    },
    {
      value: Array.from(state.areasSet),
      name: "areas",
      isSet: state.areasSet.size !== 0,
    },
    {
      value: Array.from(state.suppliersSet),
      name: "suppliers",
      isSet: state.suppliersSet.size !== 0,
    },
    {
      value: state.price,
      name: "price",
      isSet: state.priceIsSet,
    },
    {
      value: state.lengthQuery,
      name: "length",
      isSet: state.lengthIsSet,
    },
    {
      value: state.heightQuery,
      name: "height",
      isSet: state.heightIsSet,
    },
    {
      value: state.widthQuery,
      name: "width",
      isSet: state.widthIsSet,
    },
  ].reduce(
    (obj, { value, name, isSet }) =>
      isSet ? { ...obj, [name]: value.join("-") } : obj,
    query
  );

  return query;
};
